import { PaginationProps, TableProps } from 'antd';
import { deleteApi, getApi } from 'apis';
import PopUpWrapper from 'components/Global/PopUpWrapper';
import TableActionHeader from 'components/Global/TableActionHeader';
import ViewPopUpWrapper from 'components/Global/ViewPopUpWrapper';
import ConfirmDelete from 'components/Global/confirmDeleteModel';
import dayjs from 'dayjs';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toastText } from 'utils/utils';
import AddGroceriesBody from './AddMedicineBody';
import CustomTable from './CustomTable';
import ViewGroceriesBody from './ViewGroceriesBody';
import { useDebounce } from 'utils/hooks';
import { checkPermission } from 'utils/utils';


const MedicinesTable = () => {
	// State variables
	const [title, setTitle] = useState('Add Medicines');
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(
		Number(localStorage.getItem('medicineTableSize')) || 10
	);
	const [filter, setFilter] = useState('');
	const [sort, setSort] = useState('');
	const [type, setType] = useState('asc');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editSelectedMedicine, setEditSelectedMedicine] = useState<any>();
	const [totalMedicines, setTotalMedicines] = useState(0);
	const [medicinesData, setMedicinesData] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
	const [isPoppedUp, SetIsPoppedUp] = useState<boolean>(false);
	const [allUsers, setAllUsers] = useState([]);
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedDate, setSelectedDate] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
	const [isViewPopUpOpen, setIsViewPopUpOpen] = useState(false);
	const [isInitialized, setIsInitialized] = useState(false);
	const [statusFilter, setStatusFilter] = useState<'PENDING' | 'REJECTED' | 'APPROVED' | null>(null);
	const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
	const [categories, setCategories] = useState<{ id: string; categoryName: string }[]>([]);
	const [saltCompositionFilter, setSaltCompositionFilter] = useState('');

	const debouncedSearch = useDebounce(search, 500);
	const debouncedSaltComposition = useDebounce(saltCompositionFilter, 500);

	const medicinesPermissions = useSelector(
		(state: any) => state?.userProfile?.data
	);
	const viewHandler = (grocery: any) => {
		setEditSelectedMedicine(grocery);
		setTitle('Medicine Details');
		setIsViewPopUpOpen(true);
	};


	const [optionsState, setOptionsState] = useState({
		medicineName: [],
		manufacturer: [],
		marketedBy: [],
		weightage: [],
		flavors: [],
		offers: [],
		saltComposition: [],
		usesTags: [],
		numberOfItems: [],
	});
	const openDrawerHandler = () => {
		setTitle('Add Medicines');
		setEditSelectedMedicine(null);
		SetIsPoppedUp(true);
	};

	const closeDrawerByAnimation = () => {
		SetIsPoppedUp(false)
		setOptionsState({
			medicineName: [],
			manufacturer: [],
			marketedBy: [],
			weightage: [],
			flavors: [],
			offers: [],
			saltComposition: [],
			usesTags: [],
			numberOfItems: [],
		})
		setEditSelectedMedicine(null)
	};

	const performSearchHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setSearch(value);
	};

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch, statusFilter, categoryFilter, debouncedSaltComposition]);

	const showModal = () => {
		setIsModalOpen(true);
	};

	const handleOk = () => {
		setIsModalOpen(false);
		setEditSelectedMedicine(null);
	};

	const handleCancel = () => {
		setIsModalOpen(false);
		setEditSelectedMedicine(null);
	};

	const deleteHandler = async () => {
		if (editSelectedMedicine) {
			try {
				setIsDeleteLoading(true);
				const resp = await deleteApi(`/medicine/delete/${editSelectedMedicine?.id}`);
				toastText(resp?.data?.message ?? 'Medicine deleted successfully', 'success');
			} catch (error: any) {
				toastText(error.response?.data?.message ?? 'Something went wrong while deleting medicine', 'error');
			} finally {
				setIsDeleteLoading(false);
				handleCancel();
				fetchMedicines();
			}
		}
	};

	const fetchMedicines = useCallback(async () => {
		setIsLoading(true);
		const query: Record<string, unknown> = {
			page: page,
			limit: limit,
			search: debouncedSearch,
			sort: sort,
			type: type,
			filter: filter,
			userId: selectedUser,
			selectedDate,
		};
		if (statusFilter) query.status = statusFilter;
		if (categoryFilter) query.categoryId = categoryFilter;
		const salt = debouncedSaltComposition.trim();
		if (salt) query.saltComposition = salt;
		try {
			const res = await getApi('/medicine/get-all', query);
			setMedicinesData(res?.data?.data || []);
			setTotalMedicines(res?.data?.total || 0);
		} catch (error: any) {
			toastText(error?.response?.data?.error?.errorDescription, 'error');
		}
		finally {
			setIsLoading(false);
		}
	}, [page, limit, debouncedSearch, debouncedSaltComposition, type, sort, filter, selectedUser, selectedDate, statusFilter, categoryFilter]);

	const handleTableChange: TableProps<any>['onChange'] = (
		pagination,
		filters,
		sorter
	) => {
		const sortOrder = Array.isArray(sorter)
			? sorter[0]?.order
			: sorter.order === 'ascend'
				? 'asc'
				: 'desc';
		setType(sortOrder as string);

		const sortField = Array.isArray(sorter)
			? sorter[0]?.field
			: sorter.field === 'name'
				? 'firstName'
				: sorter.field;
		setSort(sortField as string);
	};

	const onShowSizeChange: PaginationProps['onShowSizeChange'] = (
		current,
		pageSize
	) => {
		setPage(current);
		setLimit(pageSize);
	};

	const fetchAllUsers = async () => {
		const isMedicinesViewAll = checkPermission(medicinesPermissions?.permissions, {
			permissionName: 'Medicines',
			permission: ['viewAll'],
		});

		if (medicinesPermissions?.isAdmin === true || isMedicinesViewAll) {
			await getApi('/user').then((res: any) => {
				if (res?.data?.responseStatus === 200) {
					const users = res?.data?.data;
					const filteredUsers = users?.map((item: any) => ({
						userId: item.userId,
						name: item.name,
						status: item.status,
					}));
					setAllUsers(filteredUsers);
				}
			}).catch(() => {});
		}
	};

	/** Fetch categories for Status/Category filters (admin only) */
	const fetchCategories = async () => {
		if (medicinesPermissions?.isAdmin !== true) return;
		try {
			const res = await getApi('/category/get-all', { limit: 1000 });
			if (res?.data?.data) {
				setCategories(res.data.data.map((c: { id: string; categoryName: string }) => ({ id: c.id, categoryName: c.categoryName })));
			}
		} catch {
			// ignore
		}
	};

	useEffect(() => {
		const isMedicinesPermission = checkPermission(medicinesPermissions.permissions, {
			permissionName: 'Medicines',
			permission: ['viewAll'],
		  });
		if (medicinesPermissions && !isInitialized) {
			if (medicinesPermissions?.isAdmin === true || isMedicinesPermission) {
				setSelectedUser(null);
			} else {
				setSelectedUser(medicinesPermissions?.id);
			}
			setIsInitialized(true);
		}
	}, [medicinesPermissions, isInitialized]);
	
	useEffect(() => {
		if (isInitialized) {
			fetchMedicines();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps -- fetchMedicines is stable
	}, [limit, page, debouncedSearch, debouncedSaltComposition, sort, type, filter, selectedUser, selectedDate, statusFilter, categoryFilter, isInitialized]);

	useEffect(() => {
		const isMedicinesViewAll = checkPermission(medicinesPermissions?.permissions, {
			permissionName: 'Medicines',
			permission: ['viewAll'],
		});
		if (medicinesPermissions?.isAdmin === true || isMedicinesViewAll) {
			fetchAllUsers();
		}
		if (medicinesPermissions?.isAdmin === true) {
			fetchCategories();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps -- fetchAllUsers/fetchCategories are stable
	}, [medicinesPermissions]);

	return (
		<>
			<div >
				<TableActionHeader title="Medicines">
				</TableActionHeader>

				<CustomTable
					dataSource={medicinesData}
					handleTableChange={handleTableChange}
					onShowSizeChange={onShowSizeChange}
					isLoading={isLoading}
					search={search}
					totalRecords={totalMedicines}
					performSearchHandler={performSearchHandler}
					performFilterHandler={(value: any) => setFilter(value)}
					openDrawerHandler={openDrawerHandler}
					setEditSelectedMedicine={setEditSelectedMedicine}
					showModal={showModal}
					currentPage={page}
					pageSize={limit}
					setSearch={setSearch}
					setTitle={setTitle}
					allUsers={allUsers}
					setSelectedUser={setSelectedUser}
					selectedUser={selectedUser}
					selectedDate={selectedDate}
					setSelectedDate={setSelectedDate}
					viewHandler={viewHandler}
					statusFilter={statusFilter}
					onStatusFilterChange={setStatusFilter}
					categoryFilter={categoryFilter}
					onCategoryFilterChange={setCategoryFilter}
					categories={categories}
					saltCompositionFilter={saltCompositionFilter}
					onSaltCompositionFilterChange={setSaltCompositionFilter}
				/>
			</div>

			<ConfirmDelete
				handleCancel={handleCancel}
				handleOk={handleOk}
				isModalOpen={isModalOpen}
				deleteHandler={deleteHandler}
				isLoading={isDeleteLoading}
				selectedDeleteData={editSelectedMedicine?.medicineName}
				deleteHeading="Medicine"
			/>

			{isPoppedUp &&
				<PopUpWrapper
					title={title}
					isPoppedUp={isPoppedUp}
					closeDrawerByAnimation={closeDrawerByAnimation}
				>
					<AddGroceriesBody
						closeDrawerByAnimation={closeDrawerByAnimation}
						editSelectedMedicine={editSelectedMedicine}
						setEditSelectedMedicine={setEditSelectedMedicine}
						fetchMedicines={fetchMedicines}
						setOptionsState={setOptionsState}
						optionsState={optionsState}
					/>
				</PopUpWrapper>
			}

			<div>
				{isViewPopUpOpen && (
					<ViewPopUpWrapper
						title={title}
						isPoppedUp={isViewPopUpOpen}
						closeDrawerByAnimation={() => {
							setIsViewPopUpOpen(false);
							setEditSelectedMedicine(null);
						}}
					>
						<ViewGroceriesBody
							medicine={editSelectedMedicine}
						/>
					</ViewPopUpWrapper>
				)}
			</div>


		</>
	);
};

export default MedicinesTable;
